import { useMemo, useState, type ReactNode } from 'react'
import { Callout, Kalender, KalenderLegend, ListEmpty, LoadingState } from 'admination-react'
import { KohortenSelect } from '@/core/components/dashboard/KohortenSelect'
import { filterCoachingsByKohorte } from '@/core/lib/coachingFilter'
import { kohortenVon } from '@/core/lib/kohorte'
import type { SortState } from '@/core/lib/viewInput'
import { useApp } from '@/core/state/appStore'
import { ListToolbar } from '@/core/views/ListToolbar'
import { useViewInput } from '@/core/views/useViewInput'
import type { ViewPipelineConfig } from '@/core/views/viewConfig'
import type { AnwesenheitPerson } from '../../api/anwesenheit'
import { useAnwesenheitAktionen } from './AnwesenheitAktionen'
import { anwesenheitLegende, anwesenheitZelle, useAnwesenheitsliste, useKalenderZeitraum, zuweisungHinweis } from './anwesenheitKalender'

const PERSONEN_CONFIG: ViewPipelineConfig<AnwesenheitPerson> = {
  rowKey: (person) => person.coaching_id,
  sortKeys: [
    { key: 'name', label: "Name", accessor: (person) => person.name },
    { key: 'unentschuldigt', label: "Unentschuldigt", accessor: (person) => person.unentschuldigt },
  ],
  searchFields: (person) => [person.name],
  extraFilter: (personen, filter) => filterCoachingsByKohorte(personen, filter as { kohorte: string | null }),
}
const KEINE_PERSONEN: AnwesenheitPerson[] = []

export function useAnwesenheitSeite(
  aktiv: boolean,
  kohorte: string | null,
  setKohorte: (beginn: string | null) => void,
): { tools: ReactNode; body: ReactNode } {
  const { dispatch } = useApp()
  const { modus, zeitraum, steuerung, monatsAnker } = useKalenderZeitraum('woche')
  const stand = useAnwesenheitsliste(aktiv, modus, zeitraum)
  const aktionen = useAnwesenheitAktionen()
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' })
  const [suche, setSuche] = useState('')
  const personen = stand.status === 'bereit' ? stand.personen : KEINE_PERSONEN
  const kohorten = useMemo(() => kohortenVon(stand.zuletzt), [stand.zuletzt])
  const aktiveKohorte = kohorten.some((k) => k.beginn === kohorte) ? kohorte : null
  const viewState = useMemo(() => ({ sort, search: suche, filter: { kohorte: aktiveKohorte } }), [sort, suche, aktiveKohorte])
  const { rows } = useViewInput(personen, PERSONEN_CONFIG, viewState)

  const tools = <>
    <ListToolbar className="calendar-tools" config={PERSONEN_CONFIG} filterRows={[]} filterActive={false} sort={sort} onSortChange={setSort} search={suche} onSearchChange={setSuche} />
    {steuerung}
    <KohortenSelect kohorten={kohorten} value={aktiveKohorte} onChange={setKohorte} laedt={stand.status === 'laedt'} />
  </>
  const body = <div className="calendar-scroll" data-testid="anwesenheit">
    {stand.status === 'laedt' && <LoadingState>Die Anwesenheitsliste wird geladen …</LoadingState>}
    {stand.status === 'fehler' && <Callout tone="error" role="alert" className="calendar-message" title={stand.meldung} />}
    {stand.status === 'bereit' && (rows.length === 0 ? <ListEmpty>Keine Teilnehmenden</ListEmpty> : <>
      <Kalender
        aria-label="Anwesenheit"
        density={stand.ansicht.modus === 'woche' ? 'week' : 'month'}
        rowLabel="Teilnehmende"
        totalLabel="Unentschuldigt"
        columns={stand.ansicht.spalten}
        rows={rows}
        rowKey={PERSONEN_CONFIG.rowKey}
        rowName={(person) => person.name}
        rowNote={zuweisungHinweis(stand.ansicht.zeitraum)}
        onRowOpen={(person) => dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'personal', tab: 'anwesenheit', coachingId: person.coaching_id, kalenderAnker: monatsAnker } })}
        cell={anwesenheitZelle(stand.ansicht.modus, aktionen.eintragOeffnen)}
        rowActions={aktionen.zeilenMenue}
        total={(person) => person.unentschuldigt}
      />
      <KalenderLegend items={anwesenheitLegende(stand.ansicht.modus)} />
    </>)}
    {aktionen.dialog}
  </div>
  return { tools, body }
}
